import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { compact, get, uniq } from 'lodash';
import { differenceInSeconds, format } from 'date-fns';
import { Box, Tooltip } from '@mui/material';
import ResultsChart from './ResultsChart';
import { joinParams } from 'utils/study';
import { formatTime } from 'utils/hooks/useDate';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import GridDetailIcon from '@mui/icons-material/Description';
import { encryptId } from 'utils/hooks/useApp';
import { useNavigate } from 'react-router';
import { testStateFormatter } from './_formatter';
import styles from 'components/SearchResultsMUI/_styles';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';

const muiGridKey = 'globalStatsExecutedTestMui2';

const GlobalStatsExecutedTests: React.FC<any> = ({ successRate, testExecutions, dataTour }) => {
  const { t } = useTranslation('Tests');
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);
  const navigate = useNavigate();

  const onCustomEntityDetail = useCallback(
    (row: any) => {
      navigate(`/tests/results/${encryptId(get(row, 'id'))}`, {
        state: { linkBack: 'globalStats' },
      });
    },
    [navigate],
  );

  const getName = (row: any) => get(row, 'test.name', '') || '';

  const { rows, uniqStates } = useMemo(() => {
    const allStates: string[] = [];
    const mappedRows = (testExecutions || []).map((item: any) => {
      const stateTranslated = t(get(item, 'state', '').toLowerCase());
      allStates.push(stateTranslated);

      const differenceFormatted = formatTime(
        differenceInSeconds(
          new Date(get(item, 'finishedWhen')),
          new Date(get(item, 'startedWhen')),
        ),
        t,
      );

      return {
        ...item,
        stateTranslated,
        testLengthModified: new Date(`1970-01-01T${differenceFormatted}`),
      };
    });

    return {
      rows: mappedRows,
      uniqStates: uniq(compact(allStates)),
    };
  }, [t, testExecutions]);

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns([
          {
            field: 'actions',
            headerName: t('Grid:actions'),
            type: 'actions',
            hideable: false,
            width: 100,
            headerAlign: 'left',
            align: 'left',
            getActions: ({ row }: GridRowParams) => [
              <GridActionsCellItem
                icon={
                  <Tooltip title={t('Grid:testResultDetail')}>
                    <GridDetailIcon />
                  </Tooltip>
                }
                label="Detail"
                onClick={() => onCustomEntityDetail(row)}
              />,
            ],
          },
          {
            field: 'executedBy',
            headerName: t('executedBy'),
            valueGetter: (value: any, row: any) =>
              joinParams([get(row, 'executedBy.firstName'), get(row, 'executedBy.lastName')]),
          },
          {
            field: 'name',
            headerName: t('name'),
            hideable: false,
            renderCell: ({ row }: GridRenderCellParams) => (
              <Tooltip title={getName(row)}>
                <Box onClick={() => onCustomEntityDetail(row)} sx={styles.gridLink}>
                  {getName(row)}
                </Box>
              </Tooltip>
            ),
            valueGetter: (value: any, row: any) => getName(row),
          },
          {
            field: 'stateTranslated',
            headerName: t('testState'),
            type: 'singleSelect',
            valueOptions: uniqStates,
            renderCell: ({ row }: GridRenderCellParams) => testStateFormatter(row),
          },
          {
            field: 'correctAnswers',
            headerName: t('correctAnswers'),
            valueGetter: (value: any, row: any) =>
              `${get(row, 'correctAnswers')}/${get(row, 'questionsCount')}`,
          },
          {
            field: 'startedWhen',
            headerName: t('startedWhen'),
            type: 'date',
            valueGetter: (value: any, row: any) => {
              try {
                return new Date(get(row, 'startedWhen', ''));
              } catch (e) {
                return null;
              }
            },
            renderCell: ({ row }: GridRenderCellParams) =>
              get(row, 'startedWhen', '')
                ? format(new Date(get(row, 'startedWhen', '')), 'dd.MM.yyyy HH:mm')
                : '',
          },
          {
            field: 'testLengthModified',
            headerName: t('testLength'),
            valueGetter: (value: any, row: any) => value && new Date(value),
            valueFormatter: (value: any) => value && `${format(value, 'HH:mm:ss')} ${t('hrs')}`,
          },
        ]),
      ),
    [
      injectColumnWidthsIntoColumns,
      onCustomEntityDetail,
      reorderColumnsByGridSettings,
      t,
      uniqStates,
    ],
  );

  return (
    <>
      {testExecutions && testExecutions.length > 0 && <ResultsChart successRate={successRate} />}
      <MuiGrid gridKey={muiGridKey} rows={rows} columns={columns} dataTour={dataTour} />
    </>
  );
};

export default GlobalStatsExecutedTests;
